import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      {/* Header */}
      <nav className="fixed top-0 w-full z-40 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/welcome')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pt-24 pb-16">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-teal-500" />
          <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
        </div>
        
        <p className="text-slate-400 mb-8">Last updated: February 2026</p>

        <div className="space-y-8 text-slate-300 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
            <p>WellNest Technologies Pvt. Ltd. ("we", "our", or "us") operates the WellNest Technologies platform (wellnesttechnologies.com). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our spa management SaaS platform.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
            <h3 className="text-lg font-medium text-teal-400 mb-2">2.1 Information You Provide</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Account information (name, email, phone number, business details)</li>
              <li>Payment information (processed securely via Razorpay)</li>
              <li>Business data (bookings, therapist information, customer records)</li>
              <li>Communications with our support team</li>
            </ul>
            
            <h3 className="text-lg font-medium text-teal-400 mb-2 mt-4">2.2 Automatically Collected Information</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Device and browser information</li>
              <li>IP address and location data</li>
              <li>Usage patterns and feature interactions</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide and maintain our spa management platform</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative notifications and updates</li>
              <li>Respond to customer service requests</li>
              <li>Analyze usage to improve our services</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Data Security</h2>
            <p>We implement industry-standard security measures including:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>SSL/TLS encryption for all data transmission</li>
              <li>Encrypted database storage</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication</li>
              <li>Secure cloud infrastructure (MongoDB Atlas, Railway)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Data Sharing</h2>
            <p>We do not sell your personal information. We may share data with:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Payment processors (Razorpay) for transaction processing</li>
              <li>Cloud service providers for hosting and infrastructure</li>
              <li>Communication providers (WhatsApp, Email) as configured by you</li>
              <li>Legal authorities when required by law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Multi-Tenant Data Isolation</h2>
            <p>Our platform uses strict multi-tenant data isolation. Each business (tenant) has completely separated data that cannot be accessed by other tenants. Your customer and business data is visible only to your authorized users.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data in a portable format</li>
              <li>Opt-out of marketing communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Data Retention</h2>
            <p>We retain your data for as long as your account is active or as needed to provide services. Upon account deletion, we will delete or anonymize your data within 90 days, except where retention is required by law.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Contact Us</h2>
            <p>For privacy-related inquiries, contact us at:</p>
            <div className="mt-2 p-4 bg-slate-900 rounded-lg">
              <p><strong>Email:</strong> privacy@wellnesttechnologies.com</p>
              <p><strong>Address:</strong> Agra, Uttar Pradesh, India</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
